import { constants, getFileSize, hasAcceptedFileType } from '@trqk/utils';

import { AddedWrongFileType } from '../components/toastMessages';
import { AudioFileTooLarge } from '../components/toastMessages/uploadAudio';

const { MAX_AUDIO_FILE_SIZE_MB, SUPPORTED_AUDIO_FILE_TYPES } = constants;

interface CheckAudioFilesInput {
    /**
     * The add() method returned by the useToast hook
     */
    addToast: AddToast;
    /**
     * Array of {@link File} objects to check for valid file size and type
     */
    files: File[];
}

/**
 * Check an array of {@link File} objects for audio files that can be uploaded. File checks happen
 * in the following order:
 * 1. If any file is too large, a toast is shown to warn the user about file size and type
 * 2. If any file has the wrong file type, a toast is shown to warn the user about the file type
 * If neither of the previous two error cases occurs, and there is at least one valid file, it is
 * returned. If not, null is returned.
 * @param files - array of {@link File} objects to check for valid file size and type
 * @returns array of {@link File} objects that have the correct file size and type, or null if no
 * valid files were found
 */
export const checkAudioFiles = ({
    addToast,
    files,
}: CheckAudioFilesInput): Maybe<File[]> => {
    const filesTooBig: File[] = [];
    const filesWithWrongType: File[] = [];
    const validFiles: File[] = [];

    files.forEach(file => {
        const tooBig =
            getFileSize({ bytes: file.size, unit: 'MB' }) >
            MAX_AUDIO_FILE_SIZE_MB;

        const wrongType = !hasAcceptedFileType(
            file,
            SUPPORTED_AUDIO_FILE_TYPES,
        );

        if (tooBig) {
            filesTooBig.push(file);
        }

        if (wrongType) {
            filesWithWrongType.push(file);
        }

        if (!tooBig && !wrongType) {
            validFiles.push(file);
        }
    });

    if (filesTooBig.length > 0) {
        addToast({
            RenderContent: AudioFileTooLarge,
            kind: 'warning-card',
        });
    } else if (filesWithWrongType.length > 0) {
        addToast({
            RenderContent: AddedWrongFileType,
            renderContentInput: {
                acceptedFileTypes: SUPPORTED_AUDIO_FILE_TYPES,
                filesWithWrongType: filesWithWrongType.length,
            },
            kind: 'warning-card',
        });
    } else if (validFiles.length > 0) {
        return validFiles;
    }

    return null;
};
